import type { FC } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { List, ListItem, ListItemText, Typography } from '@mui/material';

import { PermissionGroup } from 'src/constants/modulePermission';
import type { BaseUser, User } from 'src/types/user';
import { BulkEditContext } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

import { styles } from './index.styles';

export interface BulkEditUserRoleSummaryProps {
  changedAttributes: [string, string][];
  admin: User;
}

const BulkEditUserRoleSummary: FC<BulkEditUserRoleSummaryProps> = ({
  changedAttributes,
  admin,
}) => {
  const { t } = useTranslation('admin');
  const { formFields, selectedUsers } = useContext(BulkEditContext);
  const [fieldName, fieldValue] = changedAttributes.find(
    (attribute) =>
      attribute.includes('role') || attribute.includes('permissionGroup'),
  ) || [undefined, ''];
  const field = formFields.find((attribute) => attribute.name === fieldName);
  const choice = field?.choices.find((choice) => choice.value === fieldValue);

  if (!(selectedUsers?.length && choice)) return null;

  let usersInvalidRole: BaseUser[] = [];
  switch (fieldName) {
    case 'role': {
      const newRole = admin.role?.canEditRoles.find(
        ({ id }) => id === Number(fieldValue),
      );
      if (!newRole || newRole.canBeAdmin) break;

      // Filter all users who used to be able to become admin
      usersInvalidRole = selectedUsers.filter(({ role }) => role?.canBeAdmin);
      break;
    }
    case 'permissionGroup':
      if (Number(fieldValue) === PermissionGroup.ADMIN)
        usersInvalidRole = selectedUsers.filter(
          ({ role }) => !role?.canBeAdmin,
        );
      break;
  }

  if (!usersInvalidRole.length) return null;

  return usersInvalidRole.length < 10 ? (
    <>
      <Typography variant="h4">
        {t('admin:BulkEdit.ConfirmationHeaderPermissionsRole')}
      </Typography>
      <List>
        {usersInvalidRole.map((user, index: number) => (
          <ListItem sx={styles.listItem} dense key={index} disablePadding>
            <ListItemText>
              {user.first_name} {user.last_name}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    </>
  ) : (
    <>
      <Typography variant="h4">
        {t('admin:BulkEdit.ConfirmationHeaderPermissionsRoleOverThreshold', {
          count: usersInvalidRole.length,
        })}
      </Typography>
    </>
  );
};

export default BulkEditUserRoleSummary;
